/**
 * Space — 单个项目：派新任务的输入框 + 最近 session 列表。
 */

import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { ApiError, createSpaceTask, listSpaceSessions, sendVoiceTask, type AttachmentRef, type SessionOut } from '../lib/api'
import { clearToken } from '../lib/auth'
import { useDraft } from '../lib/useDraft'
import { useLang } from '../contexts/LanguageContext'
import { Spinner, CenterNote } from '../components/ui'
import Composer from '../components/Composer'
import { IconBack, IconChevronRight } from '../components/icons'

function shortTime(iso: string, locale: string): string {
  const d = new Date(iso)
  const sameDay = new Date().toDateString() === d.toDateString()
  return d.toLocaleString(locale, {
    month: sameDay ? undefined : '2-digit',
    day: sameDay ? undefined : '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Space() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, lang } = useLang()
  const spaceTitle = (location.state as { title?: string } | null)?.title || t('space.fallbackTitle')

  const [sessions, setSessions] = useState<SessionOut[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [prompt, setPrompt] = useDraft(`space:${id ?? 'unknown'}`)
  const [submitting, setSubmitting] = useState(false)

  const refresh = useCallback(async () => {
    if (!id) return
    try {
      const res = await listSpaceSessions(id)
      setSessions(res.sessions)
      setError(null)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken()
        navigate('/pair', { replace: true })
        return
      }
      if (e instanceof ApiError && e.status === 404) {
        navigate('/spaces', { replace: true })
        return
      }
      setError((e as Error)?.message?.slice(0, 200) || t('common.loadFailed'))
    }
  }, [id, navigate, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // 每 5s 轮询 session 列表，看新派的任务
  useEffect(() => {
    const timer = setInterval(() => void refresh(), 5_000)
    return () => clearInterval(timer)
  }, [refresh])

  const handleSend = useCallback(async (attachments: AttachmentRef[] = []) => {
    if (!id || (!prompt.trim() && attachments.length === 0) || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await createSpaceTask(id, prompt.trim(), attachments)
      setPrompt('')
      navigate(`/sessions/${res.session_id}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError(t('space.offlineHint'))
      } else {
        setError((e as Error)?.message?.slice(0, 200) || t('space.sendFailed'))
      }
    } finally {
      setSubmitting(false)
    }
  }, [id, prompt, submitting, navigate, t])

  // 语音新建任务 — 把音频发给桌面，桌面转写后直接派；成功即进会话页
  const handleSendVoice = useCallback(async (audio: Blob, onProgress: (frac: number) => void) => {
    if (!id) return
    setError(null)
    try {
      const res = await sendVoiceTask(id, audio, onProgress)
      navigate(`/sessions/${res.session_id}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setError(t('space.offlineHint'))
      else if (e instanceof ApiError && e.status === 413) setError(t('voice.tooLong'))
      else setError((e as Error)?.message?.slice(0, 200) || t('space.sendFailed'))
    }
  }, [id, navigate, t])

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-line-subtle bg-canvas/92 px-2 pb-3 pr-4 pt-safe backdrop-blur">
        <button
          onClick={() => navigate('/spaces')}
          aria-label={t('common.back')}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-muted active:bg-hover"
        >
          <IconBack className="h-5 w-5" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-[15px] font-medium text-primary">{spaceTitle}</h1>
      </header>

      <div className="flex-1 px-4 pt-4">
        {/* 派新任务 */}
        <div className="rounded-lg border border-line-subtle bg-panel p-3">
          <label className="mb-2 block text-xs text-muted">{t('space.composerLabel')}</label>
          <Composer
            value={prompt}
            onChange={setPrompt}
            onSend={(atts) => void handleSend(atts)}
            onSendVoice={handleSendVoice}
            sending={submitting}
            placeholder={t('space.composerPlaceholder')}
            menuPlacement="down"
            draftKey={`space:${id ?? 'unknown'}`}
          />
        </div>

        {error && (
          <div className="reveal mt-3 rounded-lg border border-error/30 bg-error/10 px-3.5 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* 最近会话 */}
        <div className="mb-2 mt-6 px-1 text-xs font-medium uppercase tracking-wider text-dimmed">
          {t('space.recentSessions')}
        </div>

        {sessions === null && (
          <CenterNote>
            <Spinner className="h-6 w-6 text-accent" />
          </CenterNote>
        )}

        {sessions && sessions.length === 0 && (
          <div className="reveal-in rounded-lg border border-dashed border-line px-6 py-8 text-center text-sm text-dimmed">
            {t('space.noSessions')}
          </div>
        )}

        {sessions && sessions.length > 0 && (
          <ul className="space-y-2 pb-8">
            {sessions.map((s, i) => (
              <li key={s.id} className="reveal" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                <button
                  onClick={() => navigate(`/sessions/${s.id}`)}
                  className="flex w-full items-center gap-3 rounded-lg border border-line-subtle bg-panel px-4 py-3 text-left transition-colors active:bg-elevated min-h-tap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-primary">
                      {s.title || t('space.sessionFallback', { id: s.id.slice(0, 8) })}
                    </div>
                    <div className="mt-0.5 text-xs text-dimmed">
                      {shortTime(s.created_at, lang === 'zh' ? 'zh-CN' : 'en-US')}
                    </div>
                  </div>
                  <IconChevronRight className="h-4 w-4 shrink-0 text-faint" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
